import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import api, { analyticsAPI } from '../services/api';
import Layout from './Layout';
import {
  ChartBarIcon,
  UsersIcon,
  PhoneIcon,
  DocumentIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BuildingOfficeIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import TodayTasksDonutCard from './Dashboard/TodayTasksDonutCard';

interface BranchStats {
  branchId: number;
  branchName: string;
  branchCode: string;
  isHeadOffice: boolean;
  totalLeads: number;
  leadsThisMonth: number;
  leadsThisWeek: number;
  leadsToday: number;
  wonLeads: number;
  lostLeads: number;
  activeLeads: number;
  totalLeadValue: number;
  avgLeadValue: number;
  totalTasks: number;
  pendingTasks: number;
  completedTasksThisMonth: number;
  totalUsers: number;
  adminUsers: number;
  totalCalls: number;
  callsThisMonth: number;
}

interface DashboardStats {
  viewingAllBranches?: boolean;
  branches?: BranchStats[];
  totals?: {
    totalLeads: number;
    leadsThisMonth: number;
    leadsThisWeek: number;
    leadsToday: number;
    wonLeads: number;
    lostLeads: number;
    activeLeads: number;
    totalLeadValue: number;
    avgLeadValue: number;
    totalTasks: number;
    pendingTasks: number;
    completedTasksThisMonth: number;
    totalUsers: number;
    totalCalls: number;
    callsThisMonth: number;
  };
  stats?: BranchStats;
}

interface TrendData {
  date: string;
  leadsCreated: number;
  leadsWon: number;
  leadsLost: number;
}

const BranchDashboard: React.FC = () => {
  const { user } = useAuth();
  const { currentBranch, isViewingAllBranches, canViewAllBranches } = useBranch();
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [trends, setTrends] = useState<TrendData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('30'); // days
  const [automationStats, setAutomationStats] = useState<{ total: number; successful: number; failed: number } | null>(null);
  const [todaysTasks, setTodaysTasks] = useState<Array<{ user_id: number; name: string; profile_picture_url?: string | null; pending: number; in_progress: number; completed: number; cancelled: number }>>([]);

  useEffect(() => {
    fetchDashboardData();
  }, [currentBranch, isViewingAllBranches, selectedPeriod]);

  useEffect(() => {
    fetchTodaysTasks();
  }, [currentBranch, isViewingAllBranches]);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      setError('');

      const [statsRes, trendsRes, automationRes] = await Promise.all([
        api.get(`/dashboard/stats?period=${selectedPeriod}`),
        api.get(`/dashboard/trends?period=${selectedPeriod}`),
        analyticsAPI.getAutomationStats({ period: selectedPeriod })
      ]);

      setDashboardStats(statsRes.data);
      setTrends(trendsRes.data.trends);
      setAutomationStats(automationRes.data);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to fetch dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTodaysTasks = async () => {
    try {
      const res = await api.get('/dashboard/todays-tasks');
      setTodaysTasks(res.data.data || []);
    } catch (e: any) {
      console.error('Error fetching today\'s tasks:', e);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getChangeIndicator = (current: number, previous: number) => {
    if (previous === 0) return { value: 0, isPositive: true };
    const change = ((current - previous) / previous) * 100;
    return { value: Math.abs(change), isPositive: change >= 0 };
  };

  // Prepare data for charts
  const pieChartData = dashboardStats && !dashboardStats.viewingAllBranches && dashboardStats.stats ? [
    { name: 'Active', value: dashboardStats.stats.activeLeads, color: '#3B82F6' },
    { name: 'Won', value: dashboardStats.stats.wonLeads, color: '#10B981' },
    { name: 'Lost', value: dashboardStats.stats.lostLeads, color: '#EF4444' }
  ] : dashboardStats?.totals ? [
    { name: 'Active', value: dashboardStats.totals.activeLeads, color: '#3B82F6' },
    { name: 'Won', value: dashboardStats.totals.wonLeads, color: '#10B981' },
    { name: 'Lost', value: dashboardStats.totals.lostLeads, color: '#EF4444' }
  ] : [];

  if (isLoading) {
    return (
      <Layout title="Dashboard" subtitle="Overview of your business performance">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading dashboard data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Dashboard" subtitle="Overview of your business performance">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="mt-2 text-red-600 hover:text-red-800 font-medium"
          >
            Try again
          </button>
        </div>
      </Layout>
    );
  }

  const stats = dashboardStats?.viewingAllBranches ? dashboardStats.totals : dashboardStats?.stats;

  return (
    <Layout title="Dashboard" subtitle="Overview of your business performance">
      <div className="space-y-6">
        {/* Branch Context */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">
              {isViewingAllBranches ? 'All Branches Overview' : `Branch: ${currentBranch?.name}`}
            </span>
          </div>
          
          {/* Period Selector */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Period:</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="120">Last 120 days</option>
            </select>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Leads */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Leads</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalLeads || 0}</p>
                <p className="text-sm text-gray-500 mt-1">
                  in last {selectedPeriod} days
                </p>
              </div>
              <ChartBarIcon className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          {/* Active Leads */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Leads</p>
                <p className="text-2xl font-bold text-blue-600">{stats?.activeLeads || 0}</p>
                <p className="text-sm text-gray-500 mt-1">
                  in last {selectedPeriod} days
                </p>
              </div>
              <ArrowTrendingUpIcon className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          {/* Won Leads */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Won Leads</p>
                <p className="text-2xl font-bold text-green-600">{stats?.wonLeads || 0}</p>
                <p className="text-sm text-gray-500 mt-1">
                  in last {selectedPeriod} days
                </p>
              </div>
              <ArrowTrendingUpIcon className="h-8 w-8 text-green-500" />
            </div>
          </div>

          {/* Lost Leads */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Lost Leads</p>
                <p className="text-2xl font-bold text-red-600">{stats?.lostLeads || 0}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {stats?.totalLeads ? formatPercentage((stats.lostLeads / stats.totalLeads) * 100) : '0%'} of total
                </p>
              </div>
              <ArrowTrendingDownIcon className="h-8 w-8 text-red-500" />
            </div>
          </div>
        </div>

        {/* Automation and Stats Row */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Automation Section */}
          {automationStats && (
            <div className="bg-white rounded-lg shadow p-3">
              {/* Header: Success rate and period */}
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-900">Automation</h3>
                <div className="flex items-center space-x-1">
                  {/* Success rate badge + bar */}
                  <div className="hidden sm:flex items-center space-x-1">
                    <span className="text-[10px] text-gray-500">Success</span>
                    <div className="w-16 h-1.5 bg-gray-100 rounded">
                      <div
                        className="h-1.5 bg-green-500 rounded"
                        style={{ width: `${Math.min(100, Math.round((automationStats.total ? (automationStats.successful / automationStats.total) * 100 : 0)))}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-gray-700">
                      {automationStats.total ? Math.round((automationStats.successful / automationStats.total) * 100) : 0}%
                    </span>
                  </div>
                  {/* Period */}
                  <div className="flex items-center text-[10px] text-gray-500">
                    <CalendarIcon className="h-3 w-3 mr-0.5" /> {selectedPeriod}d
                  </div>
                </div>
              </div>
              {/* Numbers grid */}
              <div className="grid grid-cols-3 gap-1.5">
                <div className="text-center p-1.5 rounded bg-gray-50">
                  <div className="text-[10px] text-gray-500">Total</div>
                  <div className="text-xl font-bold text-gray-900 mt-0.5">{automationStats.total}</div>
                </div>
                <div className="text-center p-1.5 rounded bg-green-50">
                  <div className="text-[10px] text-gray-500">Success</div>
                  <div className="text-xl font-bold text-green-600 mt-0.5">{automationStats.successful}</div>
                </div>
                <div className="text-center p-1.5 rounded bg-red-50">
                  <div className="text-[10px] text-gray-500">Failed</div>
                  <div className="text-xl font-bold text-red-600 mt-0.5">{automationStats.failed}</div>
                </div>
              </div>
            </div>
          )}

          {/* Tasks Stat */}
          <div className="bg-white rounded-lg shadow p-3">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-600">Tasks</p>
                <p className="text-xl font-bold text-gray-900">{stats?.totalTasks || 0}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {stats?.pendingTasks || 0} pending
                </p>
              </div>
              <DocumentIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
            </div>
          </div>

          {/* Calls Stat */}
          <div className="bg-white rounded-lg shadow p-3">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-600">Calls</p>
                <p className="text-xl font-bold text-gray-900">{stats?.totalCalls || 0}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  in last {selectedPeriod} days
                </p>
              </div>
              <PhoneIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
            </div>
          </div>

          {/* Team Stat */}
          <div className="bg-white rounded-lg shadow p-3">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-600">Team</p>
                <p className="text-xl font-bold text-gray-900">{stats?.totalUsers || 0}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {dashboardStats?.viewingAllBranches 
                    ? `Across ${dashboardStats.branches?.length || 0} branches`
                    : `${stats?.totalUsers || 0} users`
                  }
                </p>
              </div>
              <UsersIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
            </div>
          </div>
        </div>

        {/* Branch Comparison (for All Branches view) */}
        {dashboardStats?.viewingAllBranches && dashboardStats.branches && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Branch Performance Comparison</h3>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Branch
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Leads
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Active
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Won
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Conversion
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Users
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Value
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dashboardStats.branches.map((branch) => (
                      <tr key={branch.branchId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {branch.branchName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {branch.branchCode}
                                {branch.isHeadOffice && (
                                  <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                    HQ
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {branch.totalLeads}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {branch.activeLeads}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                          {branch.wonLeads}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {branch.totalLeads > 0 
                            ? formatPercentage((branch.wonLeads / branch.totalLeads) * 100)
                            : '0%'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {branch.totalUsers}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(branch.totalLeadValue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Today's Tasks (Active Branch) */}
        <TodayTasksDonutCard rows={todaysTasks} />

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Lead Trends Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Trends</h3>
            <ResponsiveContainer width="100%" height={selectedPeriod === '30' || selectedPeriod === '90' ? 350 : 300}>
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  interval={selectedPeriod === '120' ? 'preserveStartEnd' : selectedPeriod === '90' ? 6 : selectedPeriod === '30' ? 2 : 0}
                  minTickGap={selectedPeriod === '120' ? 15 : selectedPeriod === '90' ? 10 : 5}
                  angle={selectedPeriod === '30' ? -45 : selectedPeriod === '90' ? -45 : 0}
                  height={selectedPeriod === '30' || selectedPeriod === '90' ? 80 : 60}
                  dy={10}
                  textAnchor={selectedPeriod === '30' || selectedPeriod === '90' ? 'end' : 'middle'}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                />
                <Legend />
                <Line type="monotone" dataKey="leadsCreated" stroke="#3B82F6" name="Created" />
                <Line type="monotone" dataKey="leadsWon" stroke="#10B981" name="Won" />
                <Line type="monotone" dataKey="leadsLost" stroke="#EF4444" name="Lost" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Lead Status Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Status Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>


      </div>
    </Layout>
  );
};

export default BranchDashboard;
